import {
  ArrowRight,
  Building2,
  GraduationCap,
  HeartPulse,
  Landmark,
} from "lucide-react";
import "./IND5208372.scss";

// ============================================================================
// DATA
// ============================================================================

interface Industry {
  key: string;
  icon: typeof Building2;
  name: string;
  blurb: string;
  points: string[];
  stat: { value: string; label: string };
  photo: string;
  href: string;
  available: boolean;
}

const INDUSTRIES: Industry[] = [
  {
    key: "hospitality",
    icon: Building2,
    name: "Hospitality",
    blurb:
      "Front desk, housekeeping, and channel distribution in one system, built for hotels and resorts running on tight margins and tighter staff schedules.",
    points: ["Reservations & channel manager", "Housekeeping & maintenance", "Rate & revenue management"],
    stat: { value: "18%", label: "average RevPAR increase" },
    photo: "https://images.unsplash.com/photo-1496417263034-38ec4f0b665a?w=900&q=80&auto=format&fit=crop",
    href: "/hospitality",
    available: true,
  },
  {
    key: "education",
    icon: GraduationCap,
    name: "Education",
    blurb:
      "Enrollment, grading, and student success in one portal, built for institutions tired of reconciling a SIS, an LMS, and spreadsheets that never agree.",
    points: ["Enrollment & registration", "Gradebook & transcripts", "Advising & retention signals"],
    stat: { value: "9%", label: "improvement in retention" },
    photo: "https://images.unsplash.com/photo-1523580494863-6f3031224c94?w=900&q=80&auto=format&fit=crop",
    href: "/education",
    available: true,
  },
  {
    key: "healthcare",
    icon: HeartPulse,
    name: "Healthcare & HMO",
    blurb:
      "Patient scheduling, claims, and care coordination, built for providers and health plans who need compliance handled, not bolted on.",
    points: ["Patient scheduling & records", "Claims & eligibility", "HIPAA-aligned access controls"],
    stat: { value: "—", label: "in development" },
    photo: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=900&q=80&auto=format&fit=crop",
    href: "/healthcare",
    available: false,
  },
  {
    key: "financial",
    icon: Landmark,
    name: "Financial Services",
    blurb:
      "Client accounts, compliance reporting, and reconciliation, built for firms who can't afford a dropped audit trail.",
    points: ["Account & client management", "Compliance & audit reporting", "Reconciliation workflows"],
    stat: { value: "—", label: "in development" },
    photo: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=900&q=80&auto=format&fit=crop",
    href: "/financial-services",
    available: false,
  },
];

const PROOF_STATS = [
  { value: "2", label: "industries live, with more in development" },
  { value: "11,000+", label: "teams running on the platform" },
  { value: "99.95%", label: "platform uptime, backed by SLA" },
  { value: "<3 wks", label: "average time to go live" },
];

// ============================================================================
// COMPONENT
// ============================================================================

const IND5208372: React.FC = () => {
  return (
    <div className="wrap">
      {/* HERO */}
      <section className="hero">
        <div className="hero-bg" aria-hidden="true" />
        <div className="hero-inner">
          <div className="hero-copy">
            <span className="eyebrow">Industries</span>
            <h1>Built around how your industry actually runs</h1>
            <p className="hero-sub">
              Every industry has its own workflows, compliance requirements,
              and edge cases. Instead of one generic product stretched to fit
              everyone, we build for how your team actually works.
            </p>
            <div className="hero-cta">
              <a className="btn-primary" href="#industries">
                Explore industries <ArrowRight size={16} />
              </a>
              <button className="btn-secondary">Talk to sales</button>
            </div>
          </div>

          <div className="hero-tiles" aria-hidden="true">
            {INDUSTRIES.map((ind) => (
              <div className={ind.available ? "hero-tile" : "hero-tile disabled"} key={ind.key}>
                <img src={ind.photo} alt="" />
                <div className="hero-tile-overlay">
                  <span className="hero-tile-icon">
                    <ind.icon size={16} strokeWidth={1.75} />
                  </span>
                  <span className="hero-tile-name">{ind.name}</span>
                </div>
                {!ind.available && <span className="hero-tile-soon">Soon</span>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROOF STATS */}
      <section className="stats">
        {PROOF_STATS.map((s) => (
          <div className="stat" key={s.label}>
            <div className="stat-value">{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </section>

      {/* INDUSTRY GRID */}
      <section className="section" id="industries">
        <div className="section-head">
          <span className="eyebrow">Choose your industry</span>
          <h2>Purpose-built, not retrofitted</h2>
        </div>

        <div className="industry-grid">
          {INDUSTRIES.map((ind) => (
            <div className={ind.available ? "industry-card" : "industry-card disabled"} key={ind.key}>
              <div className="industry-photo-wrap">
                <img className="industry-photo" src={ind.photo} alt="" />
                <span className="industry-icon">
                  <ind.icon size={20} strokeWidth={1.75} />
                </span>
                {!ind.available && <span className="industry-soon">Coming soon</span>}
              </div>

              <div className="industry-body">
                <h3>{ind.name}</h3>
                <p className="industry-blurb">{ind.blurb}</p>

                <ul className="industry-points">
                  {ind.points.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>

                <div className="industry-footer">
                  <div className="industry-stat">
                    <span className="industry-stat-value">{ind.stat.value}</span>
                    <span className="industry-stat-label">{ind.stat.label}</span>
                  </div>

                  {ind.available ? (
                    <a className="industry-link" href={ind.href}>
                      Explore {ind.name} <ArrowRight size={15} />
                    </a>
                  ) : (
                    <span className="industry-link disabled">
                      Notify me <ArrowRight size={15} />
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="cta-band">
        <h2>Don't see your industry?</h2>
        <p>Tell us how your team works — we'll show you what fits.</p>
        <div className="hero-cta center">
          <button className="btn-primary">
            Talk to sales <ArrowRight size={16} />
          </button>
        </div>
      </section>
    </div>
  );
};

export default IND5208372;